import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|Exams|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Exam,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateExams';
export type SPCandidateExamsLocNames =
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}TitleGradeInfoBug`
	| `${LocNameStart}ContentAlteredGradeCompleted`
	| `${LocNameStart}TitleHiddenExamSection`
	| `${LocNameStart}TitleExamSection`
	| `${LocNameStart}ButtonCreateExam`
	| `${LocNameStart}ButtonCriteria`
	| `${LocNameStart}TitleInfoBug`
	| `${LocNameStart}ContentRescored`
	| `${LocNameStart}ContentApproved`
	| `${LocNameStart}ContentSimulated`
	| `${LocNameStart}LabelScore`
	| `${LocNameStart}LabelScaledScore`
	| `${LocNameStart}LabelCode`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelStatus`
	| `${LocNameStart}LabelRegistration`
	| `${LocNameStart}LabelDataProvider`
	| `${LocNameStart}LabelGrade`
	| `${LocNameStart}LabelDate`
	| `${LocNameStart}LabelExpireDate`
	| `${LocNameStart}LabelScoreReport`
	| `${LocNameStart}LabelHideUntil`
	| `${LocNameStart}ButtonViewScoreReport`;

export const spCandidateExamsLocs: LocDefs<SPCandidateExamsLocNames> = {
	SPCandidateExamsTitlePage: {
		defaultValue: 'Exams',
		description: 'Page Title',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.Title.PageTitle',
		...sharedProperties,
	},
	SPCandidateExamsTitleGradeInfoBug: {
		defaultValue: 'Score',
		description: 'Grade Info Title',
		displayPath: `${pathStart}Grade Info Title`,
		displayType: 'Title',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.Title.GradeInfoBug',
		...sharedProperties,
	},
	SPCandidateExamsContentAlteredGradeCompleted: {
		defaultValue: 'Completed',
		description: 'Completed altered grade',
		displayPath: `${pathStart}Completted`,
		displayType: 'Content',
		legacyName: 'Exam/System.AlteredGrade',
		...sharedProperties,
	},
	SPCandidateExamsTitleHiddenExamSection: {
		defaultValue: 'Hidden',
		description: 'Hidden Exam Section Title',
		displayPath: `${pathStart}Hidden Exams`,
		displayType: 'Title',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.Title.HiddenExamSection',
		...sharedProperties,
	},
	SPCandidateExamsTitleExamSection: {
		defaultValue: 'Exams',
		description: 'Exams Section Title',
		displayPath: `${pathStart}Exams`,
		displayType: 'Title',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.Title.ExamSection',
		...sharedProperties,
	},
	SPCandidateExamsButtonCreateExam: {
		defaultValue: 'Create Exam',
		description: 'Create Exam button',
		displayPath: `${pathStart}Create Exam`,
		displayType: 'Button',
		legacyName: 'Exam/CandidateHistory.ExamList.Button.CreateExam',
		...sharedProperties,
	},
	SPCandidateExamsButtonCriteria: {
		defaultValue: 'Criteria',
		description: 'Criteria button',
		displayPath: `${pathStart}Criteria`,
		displayType: 'Button',
		legacyName: 'Exam.InformationUrl/Candidates.ProgramHistory.Exam.Button.Criteria',
		shared: false,
		htmlAllowed: false,
		feature: HubFeature.ExamInformationUrl,
		portal: 'SP',
	},
	SPCandidateExamsTitleInfoBug: {
		defaultValue: 'More Information',
		description: 'More Information bug title',
		displayPath: `${pathStart}More Information`,
		displayType: 'Title',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.Title.InfoBug',
		...sharedProperties,
	},
	SPCandidateExamsContentRescored: {
		defaultValue: 'Rescored',
		description: 'Rescored text for exam',
		displayPath: `${pathStart}Rescored`,
		displayType: 'Content',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.Content.Rescored',
		...sharedProperties,
	},
	SPCandidateExamsContentApproved: {
		defaultValue: 'Approved',
		description: 'Approved text for exam',
		displayPath: `${pathStart}Approved`,
		displayType: 'Content',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.Content.Approved',
		...sharedProperties,
	},
	SPCandidateExamsContentSimulated: {
		defaultValue: 'Simulated',
		description: 'Simulated text for exam',
		displayPath: `${pathStart}Simulated`,
		displayType: 'Content',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.Content.Simulated',
		...sharedProperties,
	},
	SPCandidateExamsLabelScore: {
		defaultValue: 'Score',
		description: 'Label for: Score.',
		displayPath: `${pathStart}Score`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.FieldLabel.Score',
		...sharedProperties,
	},
	SPCandidateExamsLabelScaledScore: {
		defaultValue: 'Scaled Score',
		description: 'Label for: Scaled Score.',
		displayPath: `${pathStart}Scaled Score`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.FieldLabel.ScoreScaled',
		...sharedProperties,
	},
	SPCandidateExamsLabelCode: {
		defaultValue: 'Code',
		description: 'Label for: Code.',
		displayPath: `${pathStart}Code`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.ColumnHeading.ExternalId',
		...sharedProperties,
	},
	SPCandidateExamsLabelName: {
		defaultValue: 'Name',
		description: 'Label for: Name.',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.ColumnHeading.Name',
		...sharedProperties,
	},
	SPCandidateExamsLabelStatus: {
		defaultValue: 'Status',
		description: 'Label for: Status.',
		displayPath: `${pathStart}Status`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.ColumnHeading.Status',
		...sharedProperties,
	},
	SPCandidateExamsLabelRegistration: {
		defaultValue: 'Registration',
		description: 'Label for: Registration.',
		displayPath: `${pathStart}Registration`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.ColumnHeading.Registration',
		...sharedProperties,
	},
	SPCandidateExamsLabelDataProvider: {
		defaultValue: 'Data Provider',
		description: 'Label for: Data Provider.',
		displayPath: `${pathStart}Data Provider`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.ColumnHeading.DataProvider',
		...sharedProperties,
	},
	SPCandidateExamsLabelGrade: {
		defaultValue: 'Grade',
		description: 'Label for: Grade.',
		displayPath: `${pathStart}Grade`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.ColumnHeading.Grade',
		...sharedProperties,
	},
	SPCandidateExamsLabelDate: {
		defaultValue: 'Date',
		description: 'Label for: Date.',
		displayPath: `${pathStart}Date`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.ColumnHeading.Date',
		...sharedProperties,
	},
	SPCandidateExamsLabelExpireDate: {
		defaultValue: 'Expire Date',
		description: 'Label for: Expire Date.',
		displayPath: `${pathStart}Expire Date`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.ColumnHeading.ExpireDate',
		...sharedProperties,
	},
	SPCandidateExamsLabelScoreReport: {
		defaultValue: 'Score Report',
		description: 'Label for: Score Report.',
		displayPath: `${pathStart}Score Report`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.ColumnHeading.ScoreReport',
		...sharedProperties,
	},
	SPCandidateExamsLabelHideUntil: {
		defaultValue: 'Hidden Until',
		description: 'Label for: Hidden Until.',
		displayPath: `${pathStart}Hidden Until`,
		displayType: 'Label',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.ColumnHeading.hideUntil',
		...sharedProperties,
	},
	SPCandidateExamsButtonViewScoreReport: {
		defaultValue: 'View',
		description: 'Button to view the score report.',
		displayPath: `${pathStart}View`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Candidates.ProgramHistory.Exams.Button.ViewScoreReport',
		shared: false,
		htmlAllowed: false,
		feature: HubFeature.ExamScoreReports,
		portal: 'SP',
	},
};

export type SPCandidateExamsLocType = LocMap<SPCandidateExamsLocNames>;
